/* ==========================================================================
   Invite Pharmacy User (Team member) Modal — PORTAL-94
   Mirrors InviteProviderModal; invites a teammate with email, name, and role.
   ========================================================================== */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import {
  PharmacyUserRole,
  PHARMACY_USER_ROLE_LABELS,
  type PharmacyUserInvite,
} from '@/api/types'

export interface InvitePharmacyUserModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: PharmacyUserInvite) => Promise<void>
  /** Roles the current user is allowed to assign (Admin: all; Manager: staff only). */
  allowedRoles: PharmacyUserRole[]
  loading?: boolean
}

type FieldName = 'email' | 'name'

function validateEmail(email: string): string | null {
  if (!email.trim()) return 'Email is required'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Please enter a valid email address'
  }
  return null
}

function validateName(name: string): string | null {
  if (!name.trim()) return 'Name is required'
  if (name.trim().length < 2) return 'Name must be at least 2 characters'
  return null
}

const ROLE_DESCRIPTIONS: Record<PharmacyUserRole, string> = {
  admin: 'Full access, including billing and managing all team members.',
  manager: 'Everything except managing other managers/admins.',
  staff:
    'Provider-related tasks, including provider catalog assignments. No catalog, billing, or team management.',
}

// Least-privilege default: Staff is assignable by every actor who can reach this
// form, so an invite sent without touching the picker grants the least access.
const DEFAULT_ROLE = PharmacyUserRole.STAFF

export function InvitePharmacyUserModal({
  open,
  onClose,
  onSubmit,
  allowedRoles,
  loading = false,
}: InvitePharmacyUserModalProps) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<PharmacyUserRole>(DEFAULT_ROLE)
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({})

  useEffect(() => {
    if (open) {
      setEmail('')
      setName('')
      setRole(
        allowedRoles.includes(DEFAULT_ROLE) ? DEFAULT_ROLE : allowedRoles[0]
      )
      setErrors({})
      setTouched({})
    }
  }, [open, allowedRoles])

  function handleBlur(field: FieldName) {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const value = field === 'email' ? email : name
    const error = field === 'email' ? validateEmail(value) : validateName(value)
    setErrors((prev) => ({ ...prev, [field]: error || undefined }))
  }

  function validateForm(): boolean {
    const next: Partial<Record<FieldName, string>> = {}
    const emailError = validateEmail(email)
    if (emailError) next.email = emailError
    const nameError = validateName(name)
    if (nameError) next.name = nameError
    setErrors(next)
    setTouched({ email: true, name: true })
    return Object.keys(next).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validateForm()) return
    await onSubmit({ email: email.trim(), name: name.trim(), role })
  }

  const roleOptions = allowedRoles.map((r) => ({
    value: r,
    label: PHARMACY_USER_ROLE_LABELS[r],
  }))

  return (
    <Modal open={open} onClose={onClose} title="Invite Team Member" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Send an invitation email. They&apos;ll receive a link to set up their
          password and can then log in to the admin portal.
        </p>

        <FormField
          label="Email Address"
          required
          error={touched.email ? errors.email : undefined}
          htmlFor="invite-user-email"
        >
          <Input
            id="invite-user-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (errors.email) setErrors((p) => ({ ...p, email: undefined }))
            }}
            onBlur={() => handleBlur('email')}
            placeholder="teammate@example.com"
            disabled={loading}
            autoComplete="off"
          />
        </FormField>

        <FormField
          label="Name"
          required
          error={touched.name ? errors.name : undefined}
          htmlFor="invite-user-name"
        >
          <Input
            id="invite-user-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (errors.name) setErrors((p) => ({ ...p, name: undefined }))
            }}
            onBlur={() => handleBlur('name')}
            placeholder="Jane Smith"
            disabled={loading}
            autoComplete="off"
          />
        </FormField>

        <FormField label="Role" required htmlFor="invite-user-role" helperText={ROLE_DESCRIPTIONS[role]}>
          <Select
            options={roleOptions}
            value={role}
            onChange={(value) => setRole(value as PharmacyUserRole)}
            disabled={loading || roleOptions.length <= 1}
          />
        </FormField>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="sm:flex-1"
          >
            Send Invitation
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default InvitePharmacyUserModal
